import { Job } from 'services/api/job';

// ─── Dropdown Constants ────────────────────────────────────────────────────────

export const CUTTER_TYPES = ['Mill Tooth', 'TCI', 'PDC', 'ADD', 'Carb Hyb.'];
export const MUD_TYPES = ['WATER BASE', 'OIL BASE', 'SYNTHETIC'];
export const RIG_TYPES = ['TRIPLE', 'DOUBLE', 'SINGLE'];
export const ROTARY_DRIVE_TYPES = ['TOP DRIVE', 'KELLY'];
export const WELL_TYPES = ['VERTICAL', 'DIRECTIONAL', 'HORIZONTAL', 'ERW'];
export const BIT_TYPES = ['PDC', 'TCI', 'Mill Tooth', 'Core Bit', 'Other'];

export const INNER_OUTER_SCALE = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];

export const DULL_CHAR_CODES = [
  'BC',
  'BT',
  'BU',
  'CC',
  'CD',
  'CI',
  'CR',
  'CT',
  'ER',
  'EFC',
  'HC',
  'JD',
  'LC',
  'LN',
  'LT',
  'NO',
  'NR',
  'OC',
  'PB',
  'PN',
  'RG',
  'RR',
  'SD',
  'SS',
  'TR',
  'WO',
  'WT'
];

export const LOCATION_CODES = ['N', 'M', 'G', 'A', '1', '2', '3'];
export const BEARING_SEALED = ['E', 'F', 'N'];
export const BEARING_UNSEALED = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];
export const GAUGE_CODES = ['IN', '1', '2', '3', '4'];

export const REASON_PULLED_CODES = [
  'BHA',
  'CM',
  'CP',
  'DMF',
  'DP',
  'DSF',
  'DST',
  'DTF',
  'FM',
  'HP',
  'HR',
  'LIH',
  'LOG',
  'PP',
  'PR',
  'RIG',
  'TD',
  'TQ',
  'TW',
  'WC',
  'WO'
];

// ─── Sub-Types ─────────────────────────────────────────────────────────────────

export interface PostJobReportRun {
  runNumber: number;
  serialNumber: string;
  urDepthIn: number | '';
  urDepthOut: number | '';
  totHoursDC: number | '';
  totHoursDrilling: number | '';
  wobLo: number | '';
  wobHi: number | '';
  rpmLo: number | '';
  rpmHi: number | '';
  torqueLo: number | '';
  torqueHi: number | '';
  spm: number | '';
  flowRate: number | '';
  mudWeight: number | '';
  viscosity: number | '';
  pumpPressure: number | '';
  psiAcrossUR: number | '';
  formationType: string;
}

export interface PostJobReportTool {
  serialNumber: string;
  toolSeries: number | '';
  toolType: string;
  cutterType: string;
  numShearScrews: number | '';
  shearScrewWtValue: number | '';
  bnJet: string;
  jetsAboveCutter: string;
  jetsBelowCutter: string;
  tfa: number | '';
}

export interface BHAComponent {
  itemNumber: number;
  component: string;
  mfg: string;
  serialNumber: string;
  od: number | '';
  id: number | '';
  fishingNeckOD: number | '';
  fishingNeckLength: number | '';
  topConnection: string;
  bottomConnection: string;
  length: number | '';
}

export interface AnnularVelocityInputs {
  pumpOutput: number | '';
  totalSPM: number | '';
  idOfHole: number | '';
  idOfCasing: number | '';
  odOfCollars: number | '';
  odOfDP: number | '';
}

export interface JobLogEntry {
  date: string;
  narrative: string;
}

export interface BitGrading {
  innerRow: string;
  outerRow: string;
  dullChar: string;
  location: string;
  bearing: string;
  gauge: string;
  otherDullChar: string;
  reasonPulled: string;
}

// ─── Main Report Type ──────────────────────────────────────────────────────────

export interface PostJobReport {
  reportStatus: 'draft' | 'submitted';

  // Step 1: Well & Tool Setup
  contractor: string;
  rigLocation: string;
  rigType: string;
  rotaryDrive: string;
  lease: string;
  block: string;
  wellNumber: number | '';
  parish: string;
  latitude: string;
  longitude: string;
  wellTarget: string;
  waterDepth: number | '';
  wellType: string;
  wellDirectional: boolean;
  maxDLS: number | '';
  maxHoleAngle: number | '';
  tightHole: boolean;
  typeOfJob: string;
  toolType: string;
  toolSeries: number | '';
  pilotHoleDia: number | '';
  openingDia: number | '';
  toolOD: number | '';

  // Step 2: Drilling Parameters
  urDepthIn: number | '';
  urDepthOut: number | '';
  totalDrillHours: number | '';
  mudType: string;
  casingSize: number | '';
  casingWeight: number | '';
  casingID: number | '';
  casingSetAt: number | '';
  casingShoeDepth: number | '';
  runs: PostJobReportRun[];

  // Step 3: Tool & Casing Config
  tools: PostJobReportTool[];
  bitSize: number | '';
  bitMfg: string;
  bitType: string;
  bitJets: string;
  bitTFA: number | '';
  mwdSize: number | '';
  mwdMfg: string;

  // Step 4: BHA
  bhaComponents: BHAComponent[];

  // Step 5: Engineering & Daily Log
  annularVelocity: AnnularVelocityInputs;
  jobLog: JobLogEntry[];

  // Step 6: Comments & Bit Grading
  comments: string;
  bitGrading: BitGrading;
}

// ─── Factory Helpers ───────────────────────────────────────────────────────────

export const createDefaultRun = (runNumber: number): PostJobReportRun => ({
  runNumber,
  serialNumber: '',
  urDepthIn: '',
  urDepthOut: '',
  totHoursDC: '',
  totHoursDrilling: '',
  wobLo: '',
  wobHi: '',
  rpmLo: '',
  rpmHi: '',
  torqueLo: '',
  torqueHi: '',
  spm: '',
  flowRate: '',
  mudWeight: '',
  viscosity: '',
  pumpPressure: '',
  psiAcrossUR: '',
  formationType: ''
});

export const createDefaultTool = (): PostJobReportTool => ({
  serialNumber: '',
  toolSeries: '',
  toolType: '',
  cutterType: 'Mill Tooth',
  numShearScrews: '',
  shearScrewWtValue: '',
  bnJet: 'BLANK',
  jetsAboveCutter: '0',
  jetsBelowCutter: '',
  tfa: ''
});

export const createDefaultBHAComponent = (
  itemNumber: number
): BHAComponent => ({
  itemNumber,
  component: '',
  mfg: '',
  serialNumber: '',
  od: '',
  id: '',
  fishingNeckOD: '',
  fishingNeckLength: '',
  topConnection: '',
  bottomConnection: '',
  length: ''
});

export const createDefaultReport = (job: Job): PostJobReport => ({
  reportStatus: 'draft',
  contractor: '',
  rigLocation:
    typeof job.location === 'object' ? job.location?.location || '' : '',
  rigType: '',
  rotaryDrive: '',
  lease: '',
  block: '',
  wellNumber: '',
  parish: '',
  latitude: '',
  longitude: '',
  wellTarget: '',
  waterDepth: '',
  wellType: '',
  wellDirectional: false,
  maxDLS: '',
  maxHoleAngle: '',
  tightHole: false,
  typeOfJob: job.jobType?.name || '',
  toolType: '',
  toolSeries: '',
  pilotHoleDia: '',
  openingDia: '',
  toolOD: '',
  urDepthIn: '',
  urDepthOut: '',
  totalDrillHours: '',
  mudType: 'WATER BASE',
  casingSize: '',
  casingWeight: '',
  casingID: '',
  casingSetAt: '',
  casingShoeDepth: '',
  runs: [createDefaultRun(1)],
  tools: [createDefaultTool()],
  bitSize: '',
  bitMfg: '',
  bitType: '',
  bitJets: '',
  bitTFA: '',
  mwdSize: '',
  mwdMfg: '',
  bhaComponents: [createDefaultBHAComponent(1)],
  annularVelocity: {
    pumpOutput: '',
    totalSPM: '',
    idOfHole: '',
    idOfCasing: '',
    odOfCollars: '',
    odOfDP: ''
  },
  jobLog: [],
  comments: '',
  bitGrading: {
    innerRow: '0',
    outerRow: '0',
    dullChar: 'NO',
    location: 'N',
    bearing: 'N',
    gauge: 'IN',
    otherDullChar: 'NO',
    reasonPulled: 'TD'
  }
});

// ─── Auto-Calculation Helpers ──────────────────────────────────────────────────

export const calcTotalInterval = (
  depthIn: number | '',
  depthOut: number | ''
): number | null => {
  if (depthIn === '' || depthOut === '') return null;
  return Number(depthOut) - Number(depthIn);
};

export const calcAvgROP = (
  interval: number | null,
  hours: number | ''
): number | null => {
  if (interval === null || hours === '' || Number(hours) === 0) return null;
  return interval / Number(hours);
};

export const calcEquivalentHoleSize = (
  openingDia: number | '',
  pilotHoleDia: number | ''
): number | null => {
  if (openingDia === '' || pilotHoleDia === '') return null;
  const val =
    Math.pow(Number(openingDia), 2) - Math.pow(Number(pilotHoleDia), 2);
  return val > 0 ? Math.sqrt(val) : null;
};

export const calcGPM = (
  pumpOutput: number | '',
  totalSPM: number | ''
): number | null => {
  if (pumpOutput === '' || totalSPM === '') return null;
  return Number(pumpOutput) * Number(totalSPM) * 42;
};

export const calcAV = (
  gpm: number | null,
  holeID: number | '',
  pipeOD: number | ''
): number | null => {
  if (gpm === null || holeID === '' || pipeOD === '') return null;
  const denom = Math.pow(Number(holeID), 2) - Math.pow(Number(pipeOD), 2);
  if (denom <= 0) return null;
  return (0.32 * gpm) / denom;
};

export const calcBHACumulative = (components: BHAComponent[]): number[] => {
  let running = 0;
  return components.map(c => {
    running += c.length === '' ? 0 : Number(c.length);
    return running;
  });
};

// ─── Step Metadata ─────────────────────────────────────────────────────────────

export interface StepMeta {
  number: number;
  label: string;
  shortLabel: string;
  icon: string;
}

export const REPORT_STEPS: StepMeta[] = [
  {
    number: 1,
    label: 'Well & Tool Setup',
    shortLabel: 'Well',
    icon: 'oil-well'
  },
  {
    number: 2,
    label: 'Drilling Parameters',
    shortLabel: 'Drilling',
    icon: 'gauge'
  },
  {
    number: 3,
    label: 'Tool & Casing Config',
    shortLabel: 'Tool',
    icon: 'wrench'
  },
  { number: 4, label: 'BHA', shortLabel: 'BHA', icon: 'list' },
  {
    number: 5,
    label: 'Engineering & Daily Log',
    shortLabel: 'Eng / Log',
    icon: 'calculator'
  },
  {
    number: 6,
    label: 'Review & Export',
    shortLabel: 'Review',
    icon: 'file-check'
  }
];

// ─── Component Props ───────────────────────────────────────────────────────────

export interface StepProps {
  reportData: PostJobReport;
  onChange: (updates: Partial<PostJobReport>) => void;
  job: Job;
}
